import { useEffect, useState } from "react";
import { toast } from "sonner";
import { listFieldOverrides, upsertFieldOverride, deleteFieldOverride, listFieldGroups } from "@/lib/api";
import { TABS } from "@/components/booking-form/registry/tabs";
import { FIELDS } from "@/components/booking-form/registry/fields";
import { useDraftOverrides } from "./useDraftOverrides";
import { useCustomItems } from "./useCustomItems";

const NULLABLE_BLANKS = ["label", "tab_key", "group_key", "help_text"];
const SAVED_PROPS = ["label", "tab_key", "position", "is_required", "is_visible", "group_key", "is_qa_trigger", "help_text"];

const sortSiblings = (list) => list.sort((a, b) => a.pos - b.pos || a.key.localeCompare(b.key));

// State + actions for the Form Designer page (per-tenant booking-form overrides).
export function useFormDesigner(clientId) {
  const [overrides, setOverrides] = useState([]);
  const [, setGroups] = useState([]); // legacy named groups, still loaded for parity
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const d = useDraftOverrides(overrides);
  const { effective, setDraftValue, draft, setDraft, savedFor, discardDraft } = d;

  useEffect(() => {
    if (!clientId) { setOverrides([]); setGroups([]); setLoading(false); return; }
    setLoading(true);
    (async () => {
      const [{ data: ovs, error: e1 }, { data: grs, error: e2 }] = await Promise.all([
        listFieldOverrides(clientId),
        listFieldGroups(clientId),
      ]);
      if (e1) toast.error(e1.message);
      if (e2) toast.error(e2.message);
      setOverrides(ovs || []);
      setGroups(grs || []);
      setDraft({});
      setLoading(false);
    })();
  }, [clientId, setDraft]);

  const tabSiblings = (tabKey) => {
    const fieldSibs = FIELDS
      .filter((f) => effective("field", f.key, "tab_key", f.tab) === tabKey)
      .map((f) => ({ scope: "field", key: f.key, pos: effective("field", f.key, "position", f.position) ?? 0 }));
    const dividerSibs = overrides
      .filter((o) => o.scope === "divider" && effective("divider", o.field_key, "tab_key", o.tab_key) === tabKey)
      .map((o) => ({ scope: "divider", key: o.field_key, pos: effective("divider", o.field_key, "position", o.position ?? 0) }));
    return { fieldSibs, all: sortSiblings([...fieldSibs, ...dividerSibs]) };
  };

  const itemTab = (scope, key) => {
    if (scope === "field") {
      const me = FIELDS.find((f) => f.key === key);
      return me ? effective("field", key, "tab_key", me.tab) : null;
    }
    const o = overrides.find((x) => x.scope === "divider" && x.field_key === key);
    return o ? effective("divider", key, "tab_key", o.tab_key) : null;
  };

  // Swap position with the neighbour (fields + dividers share one order).
  const moveItem = (scope, key, dir) => {
    const tabKey = itemTab(scope, key);
    if (tabKey === null) return;
    const siblings = tabSiblings(tabKey).all;
    const idx = siblings.findIndex((s) => s.scope === scope && s.key === key);
    const nbrIdx = dir === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || nbrIdx < 0 || nbrIdx >= siblings.length) return;
    const a = siblings[idx], b = siblings[nbrIdx];
    setDraftValue(a.scope, a.key, "position", b.pos);
    setDraftValue(b.scope, b.key, "position", a.pos);
  };

  // Indent joins the head field above (group_key = head key); outdent clears it.
  const toggleIndent = (fieldKey, dir) => {
    if (dir === "out") { setDraftValue("field", fieldKey, "group_key", ""); return; }
    const tabKey = itemTab("field", fieldKey);
    if (tabKey === null) return;
    const siblings = sortSiblings(tabSiblings(tabKey).fieldSibs);
    const idx = siblings.findIndex((s) => s.key === fieldKey);
    if (idx <= 0) return;
    const prev = siblings[idx - 1];
    setDraftValue("field", fieldKey, "group_key", effective("field", prev.key, "group_key", "") || prev.key);
  };

  const buildRow = (scope, field_key) => {
    const row = { id: (savedFor(scope, field_key) || {}).id, client_id: clientId, scope, field_key };
    SAVED_PROPS.forEach((p) => { row[p] = effective(scope, field_key, p, null); });
    NULLABLE_BLANKS.forEach((p) => { if (row[p] === "") row[p] = null; });
    return row;
  };

  const saveAll = async () => {
    setSaving(true);
    const rows = Object.keys(draft)
      .filter((dk) => draft[dk] && Object.keys(draft[dk]).length)
      .map((dk) => {
        // Split on FIRST colon only — slot_label keys contain "::".
        const at = dk.indexOf(":");
        const field_key = dk.slice(at + 1);
        return { field_key, row: buildRow(dk.slice(0, at), field_key) };
      });
    const results = await Promise.all(rows.map(({ row }) => upsertFieldOverride(row)));
    let ok = 0, fail = 0;
    results.forEach((res, i) => {
      if (res?.error) { fail++; toast.error(`Save failed for ${rows[i].field_key}: ${res.error.message}`); }
      else ok++;
    });
    const { data } = await listFieldOverrides(clientId);
    setOverrides(data || []);
    setDraft({});
    setSaving(false);
    if (ok) toast.success(`Saved ${ok} override${ok === 1 ? "" : "s"}`);
    if (!ok && !fail) toast.info("Nothing to save");
  };

  const resetRow = async (scope, field_key) => {
    const row = savedFor(scope, field_key);
    if (row) {
      const { error: err } = await deleteFieldOverride(row.id);
      if (err) { toast.error(err.message); return; }
      setOverrides((o) => o.filter((x) => x.id !== row.id));
    }
    discardDraft(scope, field_key);
    if (row) toast.success(`Reset to default · ${field_key}`);
  };

  const custom = useCustomItems({ clientId, overrides, setOverrides, effective });

  return {
    ...d, ...custom, overrides, loading, saving, clientId,
    moveItem, toggleIndent, saveAll, resetRow,
    allTabs: [...TABS, ...custom.customTabs],
  };
}
